import { useState, useRef, useCallback, useEffect } from 'react';
import { Icons } from './Icons';
import { ConfirmModal } from './ConfirmModal';
import { AppInfo } from '../services/api';

interface SidebarProps {
  apps: AppInfo[];
  selectedApp: string | null;
  onSelectApp: (appName: string) => void;
  onAddApp?: () => void;
  onRemoveApp?: (appName: string) => void;
  onReorderApps?: (apps: AppInfo[]) => void;
  loading?: boolean;
}

export function Sidebar({ apps, selectedApp, onSelectApp, onAddApp, onRemoveApp, onReorderApps, loading }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem('sidebar-width');
    return saved ? parseInt(saved, 10) : 256;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [draggedApp, setDraggedApp] = useState<string | null>(null);
  const [dragOverApp, setDragOverApp] = useState<string | null>(null);
  const [appToRemove, setAppToRemove] = useState<string | null>(null);
  const [filterQuery, setFilterQuery] = useState('');
  const sidebarRef = useRef<HTMLDivElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);

  // Save width to localStorage
  useEffect(() => {
    if (!collapsed) {
      localStorage.setItem('sidebar-width', width.toString());
    }
  }, [width, collapsed]);

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = Math.max(180, Math.min(400, e.clientX));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Auto-focus filter input on mount
  useEffect(() => {
    filterInputRef.current?.focus();
  }, []);

  const filteredApps = filterQuery
    ? apps.filter(app => app.name.toLowerCase().includes(filterQuery.toLowerCase()))
    : apps;

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, appName: string) => {
    setDraggedApp(appName);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appName);
  };

  const handleDragOver = (e: React.DragEvent, appName: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (appName !== draggedApp) {
      setDragOverApp(appName);
    }
  };

  const handleDragLeave = () => {
    setDragOverApp(null);
  };

  const handleDrop = (e: React.DragEvent, targetAppName: string) => {
    e.preventDefault();
    setDragOverApp(null);

    if (!draggedApp || draggedApp === targetAppName || !onReorderApps) {
      setDraggedApp(null);
      return;
    }

    const draggedIndex = apps.findIndex(a => a.name === draggedApp);
    const targetIndex = apps.findIndex(a => a.name === targetAppName);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedApp(null);
      return;
    }

    const newApps = [...apps];
    const [removed] = newApps.splice(draggedIndex, 1);
    newApps.splice(targetIndex, 0, removed!);

    onReorderApps(newApps);
    setDraggedApp(null);
  };

  const handleDragEnd = () => {
    setDraggedApp(null);
    setDragOverApp(null);
  };

  return (
    <div
      ref={sidebarRef}
      className="flex flex-col border-r border-[var(--border-subtle)] relative"
      style={{
        background: 'var(--bg-secondary)',
        width: collapsed ? 64 : width,
        minWidth: collapsed ? 64 : 180,
        maxWidth: collapsed ? 64 : 400,
        transition: isResizing ? 'none' : 'width 0.2s ease',
      }}
    >
      {/* Header */}
      <div className="p-4 border-b border-[var(--border-subtle)]">
        <div className="flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <Icons.Server className="w-5 h-5 text-blue-400" />
              <span className="font-semibold text-white">Apps</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="btn-ghost p-1.5 rounded-lg"
          >
            <Icons.ChevronDown
              className={`w-4 h-4 transform transition-transform ${collapsed ? '-rotate-90' : 'rotate-90'}`}
            />
          </button>
        </div>
      </div>

      {/* Filter Input */}
      {!collapsed && (
        <div className="px-2 pt-2">
          <div className="relative">
            <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              ref={filterInputRef}
              type="text"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              placeholder="Filter apps..."
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] rounded-lg pl-8 pr-8 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
            {filterQuery && (
              <button
                onClick={() => setFilterQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[var(--bg-secondary)] text-gray-500 hover:text-gray-300 transition-colors"
              >
                <Icons.X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* App List */}
      <div className="flex-1 overflow-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icons.Spinner className="w-5 h-5 text-blue-400" />
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {!collapsed && 'No apps configured'}
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            {!collapsed && 'No matching apps'}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredApps.map((app) => {
              const isSelected = selectedApp === app.name;
              const isDragging = draggedApp === app.name;
              const isDragOver = dragOverApp === app.name;

              return (
                <div
                  key={app.name}
                  draggable={!collapsed}
                  onDragStart={(e) => handleDragStart(e, app.name)}
                  onDragOver={(e) => handleDragOver(e, app.name)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, app.name)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onSelectApp(app.name)}
                  className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30'
                      : 'hover:bg-[var(--bg-tertiary)] text-gray-300'
                  } ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'border-t-2 border-t-blue-500' : ''}`}
                  title={collapsed ? app.name : undefined}
                  style={{ cursor: !collapsed ? 'grab' : undefined }}
                >
                  {!collapsed && (
                    <span className="text-gray-600 cursor-grab">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                      </svg>
                    </span>
                  )}
                  <span
                    className={`status-dot flex-shrink-0 ${
                      app.hasData === undefined
                        ? 'status-pending'
                        : app.hasData
                          ? 'status-has-data'
                          : 'status-no-data'
                    }`}
                    title={
                      app.hasData === undefined
                        ? 'Checking data status...'
                        : app.hasData
                          ? 'Has log data'
                          : 'No log data yet'
                    }
                  />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left text-sm truncate">{app.name}</span>
                      {onRemoveApp && !isSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAppToRemove(app.name);
                          }}
                          className="p-1 rounded hover:bg-red-600/20 text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove app from monitoring"
                        >
                          <Icons.X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add App Button (Admin only) */}
      {onAddApp && (
        <div className="p-3 border-t border-[var(--border-subtle)]">
          <button
            onClick={onAddApp}
            className={`w-full btn btn-secondary text-sm ${collapsed ? 'px-2' : ''}`}
          >
            <Icons.Plus className="w-4 h-4" />
            {!collapsed && 'Add App'}
          </button>
        </div>
      )}

      {/* Resize Handle */}
      {!collapsed && (
        <div
          onMouseDown={handleMouseDown}
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500/50 transition-colors"
          style={{ background: isResizing ? 'var(--accent-blue)' : 'transparent' }}
        />
      )}

      {/* Remove App Confirmation Modal */}
      <ConfirmModal
        isOpen={!!appToRemove}
        title="Remove App"
        message={`Are you sure you want to remove "${appToRemove}" from monitoring? This will stop log collection for this app.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (appToRemove && onRemoveApp) {
            onRemoveApp(appToRemove);
          }
          setAppToRemove(null);
        }}
        onCancel={() => setAppToRemove(null)}
      />
    </div>
  );
}
